// src/pages/Zones.js

import { useState, useEffect, useRef } from 'react';
import {
  MapPin, Plus, Pencil, Trash2, X, ChevronUp, Search,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { zones as zonesApi } from '../services/api';

// Brand
const PRIMARY = '#00ACC1';

// Zone types — matches mobile app
const ZONE_TYPES = [
  { value: 1, label: 'Home' },
  { value: 2, label: 'Office' },
  { value: 3, label: 'Client' },
  { value: 4, label: 'Supplier' },
  { value: 5, label: 'Site' },
  { value: 6, label: 'Prospect' },
  { value: 7, label: 'Private' },
  { value: 8, label: 'Other' },
];

const ZONE_TYPE_EMOJI = {
  1: '🏠', 2: '🏢', 3: '👥', 4: '📦',
  5: '🔧', 6: '🔍', 7: '👤', 8: '📍',
};

// business zone types where name = client/supplier/site name
const BIZ_ZONE_TYPES = [3, 4, 5, 6];

function zoneTypeLabel(type) {
  return ZONE_TYPES.find(z => z.value === type)?.label ?? 'Zone';
}

function nameFieldLabel(type) {
  if (BIZ_ZONE_TYPES.includes(type)) return `${zoneTypeLabel(type)} Name`;
  return 'Zone Name';
}

function namePlaceholder(type) {
  if (BIZ_ZONE_TYPES.includes(type)) return 'e.g. Acme Corp';
  return 'e.g. Home, Head Office';
}

// Card
const Card = ({ children, className = '', theme }) => (
  <div className={`rounded-2xl border transition-all ${className}`}
    style={{
      backgroundColor: theme.card,
      borderColor: theme.border, 
      boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.06)',
    }}>
    {children}
  </div>
);

// Back to top
const BackToTop = () => {
  const [v, setV] = useState(false);
  useEffect(() => {
    const fn = () => setV(window.scrollY > 400);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg hover:scale-110 transition-all duration-500 ${v ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}`}
      style={{ background: `linear-gradient(135deg, ${PRIMARY}, #00838f)`, boxShadow: '0 4px 14px rgba(0,172,193,0.4)' }}>
      <ChevronUp size={24} />
    </button>
  );
};

// Address search using Nominatim (free OSM geocoding)
const AddressSearch = ({ theme, onSelect }) => {
  const [query,      setQuery]      = useState('');
  const [results,    setResults]    = useState([]);
  const [searching,  setSearching]  = useState(false);
  const [showDrop,   setShowDrop]   = useState(false);
  const timerRef = useRef(null);
  const wrapRef  = useRef(null);
  const abortRef = useRef(null);

  // close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function handleChange(val) {
    setQuery(val);
    clearTimeout(timerRef.current);
    if (val.trim().length < 3) { setResults([]); setShowDrop(false); return; }

    // debounce 500ms
    timerRef.current = setTimeout(() => doSearch(val.trim()), 500);
  }

  async function doSearch(q) {
    // abort any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setSearching(true);
    try {
      // Nominatim requires identification — use email param per their policy
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&countrycodes=za&limit=5&email=app@tripdata.co.za`;
      const res = await fetch(url, {
        signal: controller.signal,
        headers: { 'Accept-Language': 'en' },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setResults(data);
      setShowDrop(data.length > 0);
    } catch (err) {
      // only clear results if it wasn't an abort
      if (err.name !== 'AbortError') setResults([]);
    } finally {
      setSearching(false);
    }
  }

  function handleSelect(item) {
    setQuery(item.display_name.split(',').slice(0, 3).join(','));
    setShowDrop(false);
    onSelect(parseFloat(item.lat), parseFloat(item.lon), item.display_name);
  }

  return (
    <div ref={wrapRef} className="relative mb-3">
      <div className="relative">
        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: theme.textMuted }} />
        <input
          value={query}
          onChange={e => handleChange(e.target.value)}
          onFocus={() => { if (results.length > 0) setShowDrop(true); }}
          placeholder="Search address, e.g. 123 Main St, Pretoria"
          className="w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none transition-colors"
          style={{ backgroundColor: theme.background, borderColor: theme.border, color: theme.text }}
        />
        {searching && (
          <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 rounded-full animate-spin"
              style={{ borderColor: theme.border, borderTopColor: PRIMARY }} />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {showDrop && results.length > 0 && (
        <div className="absolute w-full mt-1 rounded-xl border overflow-hidden shadow-lg"
          style={{ backgroundColor: theme.card, borderColor: theme.border, zIndex: 1100 }}>
          {results.map((item, i) => (
            <button key={i}
              onClick={() => handleSelect(item)}
              className="w-full text-left px-4 py-3 text-xs transition-colors hover:opacity-80 flex items-start gap-2"
              style={{
                color: theme.text,
                borderBottom: i < results.length - 1 ? `1px solid ${theme.border}` : 'none',
              }}>
              <MapPin size={12} className="mt-0.5 shrink-0" style={{ color: PRIMARY }} />
              <span className="line-clamp-2">{item.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Leaflet map component
const ZoneMap = ({ lat, lng, radius, theme, onMapClick, mapRef }) => {
  const mapInstance = useRef(null);
  const markerRef   = useRef(null);
  const circleRef   = useRef(null);

  useEffect(() => {
    const initMap = () => {
      const el = document.getElementById('zone-map');
      if (!el || mapInstance.current) return;
      const L = window.L;
       const map = L.map(el, { zoomControl: true });
      mapInstance.current = map;
      mapRef.current = map;

      // CARTO raster tiles require an API key, attribution must stay visible
      const style = theme.isDark ? 'dark_all' : 'light_all';
      L.tileLayer(
        `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_KEY}`,
        { maxZoom: 19, attribution: '&copy; OpenStreetMap contributors &copy; CARTO' }
      ).addTo(map);

      const pinIcon = L.divIcon({
        className: '',
        html: '<div style="width:14px;height:14px;background:#FF6B00;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>',
        iconAnchor: [7, 7],
      });

      const marker = L.marker([lat, lng], { icon: pinIcon, draggable: true }).addTo(map);
      const circle = L.circle([lat, lng], {
        radius: radius, color: '#FF6B00', fillColor: '#FF6B00', fillOpacity: 0.12, weight: 2,
      }).addTo(map);

      markerRef.current = marker;
      circleRef.current = circle;

      map.setView([lat, lng], 15);

      // click map to move pin
      map.on('click', (e) => {
        const { lat: newLat, lng: newLng } = e.latlng;
        marker.setLatLng([newLat, newLng]);
        circle.setLatLng([newLat, newLng]);
        onMapClick(newLat, newLng);
      });

      // drag marker
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        circle.setLatLng([pos.lat, pos.lng]);
        onMapClick(pos.lat, pos.lng);
      });
    };

    if (window.L) {
      setTimeout(initMap, 50);
    } else {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => setTimeout(initMap, 50);
      document.head.appendChild(script);
    }

    return () => {
      if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; }
    };
  }, [theme.isDark]);

  // update marker/circle when coords or radius change externally
  useEffect(() => {
    if (markerRef.current && circleRef.current && mapInstance.current) {
      markerRef.current.setLatLng([lat, lng]);
      circleRef.current.setLatLng([lat, lng]);
      circleRef.current.setRadius(radius);
      mapInstance.current.setView([lat, lng], mapInstance.current.getZoom());
    }
  }, [lat, lng, radius]);

  return (
    <div id="zone-map" className="rounded-xl overflow-hidden"
      style={{ height: 'calc(95vh - 220px)', minHeight: 400, boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.06)' }} />
  );
};

// Main
export default function Zones() {
  const theme = useTheme();
  const mapRef = useRef(null);

  const [zoneList, setZoneList] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [editZone, setEditZone] = useState(null);

  // form state
  const [name,     setName]     = useState('');
  const [lat,      setLat]      = useState('-26.2041');
  const [lng,      setLng]      = useState('28.0473');
  const [radius,   setRadius]   = useState('200');
  const [zoneType, setZoneType] = useState(null);

  useEffect(() => { loadZones(); }, []);

  async function loadZones() {
    setLoading(true);
    const res = await zonesApi.list();
    if (res.success && res.data) setZoneList(res.data);
    setLoading(false);
  }

  function openForm(zone = null) {
    setError('');
    if (zone) {
      setEditZone(zone);
      setName(zone.name || '');
      setLat(String(zone.lat));
      setLng(String(zone.lng));
      setRadius(String(zone.radius));
      setZoneType(zone.zone_type || null);
    } else {
      setEditZone(null);
      setName('');
      setLat('-26.2041');
      setLng('28.0473');
      setRadius('200');
      setZoneType(null);
    }
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditZone(null);
    setError('');
  }

  function handleMapClick(newLat, newLng) {
    setLat(newLat.toFixed(6));
    setLng(newLng.toFixed(6));
  }

  // called when user picks an address from search
  function handleAddressSelect(newLat, newLng, displayName) {
    setLat(newLat.toFixed(6));
    setLng(newLng.toFixed(6));
    // pan map to new location
    if (mapRef.current) {
      mapRef.current.setView([newLat, newLng], 16);
    }
  }

  async function handleSave() {
    if (!zoneType) { setError('Please select a zone type.'); return; }
    if (!name.trim()) { setError('Name is required.'); return; }
    if (!lat || !lng) { setError('Coordinates are required.'); return; }

    setSaving(true);
    setError('');
    const data = {
      name:      name.trim(),
      lat:       parseFloat(lat),
      lng:       parseFloat(lng),
      radius:    parseInt(radius) || 200,
      zone_type: zoneType,
    };

    const res = editZone
      ? await zonesApi.update(editZone.id, data)
      : await zonesApi.create(data);

    setSaving(false);
    if (res.success) { closeForm(); loadZones(); }
    else setError(res.error || 'Could not save zone.');
  }

  async function handleDelete(zone) {
    if (!window.confirm(`Delete "${zone.name}"? This cannot be undone.`)) return;
    const res = await zonesApi.delete(zone.id);
    if (res.success) loadZones();
    else setError(res.error || 'Could not delete zone.');
  }

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: `1px solid ${theme.border}`, fontSize: 14, outline: 'none',
    backgroundColor: theme.background, color: theme.text,
  };

  const latN = parseFloat(lat);
  const lngN = parseFloat(lng);
  const hasCoords = !isNaN(latN) && !isNaN(lngN);

  return (
    <div className="relative px-3 sm:px-4 md:px-8 py-4 sm:py-6 md:py-8 min-h-screen"
      style={{ backgroundColor: theme.background }}>
      <div className="relative z-[1]">

        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold" style={{ color: theme.text }}>
              Zones
            </h1>
            <p className="text-[11px] sm:text-sm mt-0.5 sm:mt-1" style={{ color: theme.textMuted }}>
              {zoneList.length} zone{zoneList.length !== 1 ? 's' : ''} configured
            </p>
          </div>
          <button
            onClick={() => openForm()}
            className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-[10px] sm:text-xs font-semibold text-white transition-all hover:scale-105 active:scale-95"
            style={{ background: `linear-gradient(135deg, ${PRIMARY}, #00838f)` }}>
            <Plus size={13} />
            <span className="hidden sm:inline">Add Zone</span>
          </button>
        </div>

        {/* Zone list */}
        {loading ? (
          <div className="py-16 text-center">
            <div className="w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-3"
              style={{ borderColor: theme.border, borderTopColor: PRIMARY }} />
            <p className="text-sm" style={{ color: theme.textMuted }}>Loading zones...</p>
          </div>
        ) : zoneList.length === 0 ? (
          <Card theme={theme} className="py-16 text-center">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${PRIMARY}18` }}>
              <MapPin size={24} style={{ color: PRIMARY }} />
            </div>
            <p className="text-sm font-semibold mb-1" style={{ color: theme.text }}>No zones yet</p>
            <p className="text-xs px-8 mb-4" style={{ color: theme.textMuted }}>
              Add zones to auto-classify trips by location.
            </p>
            <button onClick={() => openForm()}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white text-sm font-bold transition-all hover:scale-105"
              style={{ background: `linear-gradient(135deg, ${PRIMARY}, #00838f)` }}>
              <Plus size={14} /> Add Zone
            </button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {zoneList.map(zone => (
              <Card key={zone.id} theme={theme} className="p-4 hover:scale-[1.01] transition-transform">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-lg"
                    style={{ backgroundColor: `${PRIMARY}18` }}>
                    {ZONE_TYPE_EMOJI[zone.zone_type] || '📍'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: theme.text }}>
                      {zone.name}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>
                      {zoneTypeLabel(zone.zone_type)} · {zone.radius}m radius
                    </p>
                    <p className="text-[10px] font-mono mt-1" style={{ color: theme.textMuted }}>
                      {parseFloat(zone.lat).toFixed(4)}, {parseFloat(zone.lng).toFixed(4)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openForm(zone)}
                      className="p-2 rounded-lg transition-opacity hover:opacity-70"
                      style={{ color: theme.textMuted }}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleDelete(zone)}
                      className="p-2 rounded-lg transition-opacity hover:opacity-70"
                      style={{ color: '#f44336' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) closeForm(); }}>
          <div className="absolute inset-0 bg-black/40" onClick={closeForm} />
          <div className="relative w-full max-w-6xl rounded-2xl p-5 shadow-2xl overflow-y-auto"
            style={{ backgroundColor: theme.card, height: 'calc(95vh - 2rem)' }}>

            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold" style={{ color: theme.text }}>
                {editZone ? 'Edit Zone' : 'New Zone'}
              </h3>
              <button onClick={closeForm} className="p-1 rounded-lg transition-opacity hover:opacity-70"
                style={{ color: theme.textMuted }}>
                <X size={16} />
              </button>
            </div>

            {/* Two-column layout: map left, form right */}
            <div className="flex flex-col lg:flex-row gap-5 flex-1 min-h-0">

              {/* Left — search + map (fills height) */}
              <div className="flex-1 min-w-0 flex flex-col">
                <AddressSearch theme={theme} onSelect={handleAddressSelect} />
                {hasCoords && (
                  <div>
                    <ZoneMap
                      lat={latN} lng={lngN} radius={parseInt(radius) || 200}
                      theme={theme} onMapClick={handleMapClick} mapRef={mapRef}
                    />
                    <p className="text-[10px] mt-1.5 text-center" style={{ color: theme.textMuted }}>
                      Search an address above, click map, or drag pin
                    </p>
                  </div>
                )}
              </div>

              {/* Right — form fields */}
              <div className="lg:w-72 shrink-0">

                {/* Zone type */}
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                  style={{ color: theme.textMuted }}>Zone Type *</p>
                <div className="flex gap-2 flex-wrap mb-4">
                  {ZONE_TYPES.map(t => (
                    <button key={t.value} onClick={() => setZoneType(t.value)}
                      className="px-3 py-1.5 rounded-full border text-xs font-semibold transition-all active:scale-95"
                      style={{
                        borderColor:     zoneType === t.value ? PRIMARY : theme.border,
                        backgroundColor: zoneType === t.value ? `${PRIMARY}18` : 'transparent',
                        color:           zoneType === t.value ? PRIMARY : theme.textMuted,
                      }}>
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Name */}
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                  style={{ color: theme.textMuted }}>
                  {zoneType ? nameFieldLabel(zoneType) : 'Zone Name'} *
                </p>
                <input value={name} onChange={e => setName(e.target.value)}
                  placeholder={zoneType ? namePlaceholder(zoneType) : 'Select zone type first'}
                  style={inputStyle} className="mb-4" />

                {/* Coords */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                      style={{ color: theme.textMuted }}>Latitude</p>
                    <input value={lat} onChange={e => setLat(e.target.value)}
                      placeholder="-26.2041" style={inputStyle} />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                      style={{ color: theme.textMuted }}>Longitude</p>
                    <input value={lng} onChange={e => setLng(e.target.value)}
                      placeholder="28.0473" style={inputStyle} />
                  </div>
                </div>

                {/* Radius */}
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                  style={{ color: theme.textMuted }}>Radius — {radius || 0}m</p>
                <input value={radius} onChange={e => setRadius(e.target.value)}
                  placeholder="200" type="number" style={inputStyle} className="mb-4" />

                {/* Error */}
                {error && (
                  <div className="mb-3 px-3 py-2.5 rounded-xl text-xs"
                    style={{ backgroundColor: theme.isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2', color: '#f87171' }}>
                    {error}
                  </div>
                )}

                {/* Save */}
                <button onClick={handleSave} disabled={saving}
                  className="w-full py-3.5 rounded-xl text-white text-sm font-bold transition-all disabled:opacity-60 active:scale-[0.98]"
                  style={{ background: `linear-gradient(135deg, ${PRIMARY}, #00838f)` }}>
                  {saving ? 'Saving...' : editZone ? 'Save Changes' : 'Add Zone'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BackToTop />
    </div>
  );
}